import {DeleteOutline} from '@mui/icons-material'
import {Badge, IconButton, TableCell, TableRow} from '@mui/material'
import {useEffect} from 'react'
import {UseFieldArrayRemove, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {ControlledTextField, ControlledSelectField} from '@kilnfeature/modules/kilnConfig/common'
import {HeatZone, HeatZoneOption} from '@kilnfeature/types'

type HeatZonesTableRowProps = {
  index: number
  remove: UseFieldArrayRemove
  numberOfRows: number
  kilnLength: number
  heatZoneOptions: HeatZoneOption[]
}

export const HeatZonesTableRow = ({
  index,
  remove,
  numberOfRows,
  kilnLength,
  heatZoneOptions
}: HeatZonesTableRowProps) => {
  const {t} = useTranslation()

  const {control, setValue, watch} = useFormContext<{heatZones: HeatZone[]}>()

  const removeRow = (rowIndex: number) => {
    remove(rowIndex)
    if (rowIndex === numberOfRows) {
      setValue(`heatZones.${rowIndex - 1}.end`, kilnLength)
    }
  }

  const isFirstRow = index === 0
  const isLastRow = index === numberOfRows

  const watchEnd = watch(`heatZones.${index}.end`)
  const watchStart = watch(`heatZones.${index}.start`)

  useEffect(() => {
    if (isLastRow) return
    if (watchEnd !== undefined && watchEnd !== null) {
      setValue(`heatZones.${index + 1}.start`, Number(watchEnd))
    }
  }, [index, isLastRow, watchEnd, setValue])

  const isInBetweenValues = (value: number) => {
    if (value <= 0) {
      return t('error.lessThanZero')
    }
    if (value >= kilnLength) {
      return t('error.moreThanKilnLength')
    }
    if (value <= watchStart) {
      return t('error.lessThanStart')
    }
    return true
  }

  return (
    <TableRow data-test-id="heat_zones_table_row">
      <TableCell align={'center'} data-test-id="heat_zones_index">
        <Badge badgeContent={index + 1} color="secondary" />
      </TableCell>
      <TableCell align={'center'} width={'55%'} data-test-id="heat_zone_name">
        <ControlledSelectField
          control={control}
          formDataName={`heatZones.${index}.name`}
          label={''}
          rules={{required: t('error.required')}}
          options={heatZoneOptions}
          className="w-full"
          dataTestId="heat_zone_name_select"
        />
      </TableCell>
      <TableCell align={'center'} width={'11%'} data-test-id="heat_zone_start">
        {watchStart}
      </TableCell>
      <TableCell align={'center'} width={'11%'} data-test-id="heat_zone_end">
        {isLastRow ? (
          <>{watchEnd}</>
        ) : (
          <ControlledTextField
            control={control}
            formDataName={`heatZones.${index}.end`}
            label={''}
            rules={{
              required: t('error.required'),
              validate: {
                isInBetweenValues
              }
            }}
            TextFieldProps={{
              type: 'number',
              fullWidth: true,
              inputProps: {
                min: watchStart,
                step: 0.1
              }
            }}
          />
        )}
      </TableCell>
      <TableCell align={'center'}>
        {!isFirstRow && (
          <IconButton onClick={() => removeRow(index)} data-test-id="heat_zone_delete_btn">
            <DeleteOutline color={'error'} />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  )
}
